import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import { getUser, saveUser, removeUser } from '../modules/userdataactions';
import { listPortfolios } from '../modules/portfoliodata';
import { getTenants } from '../modules/tenantdata';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Input from '@mui/material/Input';
import User from '../domain/user';
import { Portfolio } from '../domain/portfolio';
import ResetPasswordDialog from './resetpassworddialog';
import DeleteConfirmDialog from '../common/deleteconfirmdialog';
import { verifyPhonenumber, normalizePhonenumber } from '../common/common';
import RepresentationsList from './representationslist';
import { useTranslation } from 'react-i18next';
const _ = require('lodash');

const styles = {
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        m: 0.25,
    },
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export default function UserEditView(props) {
    const dispatch = useDispatch();
    const getUserByIdInProgress = useSelector((state) => state.userdata.getUserByIdInProgress);
    const usersById = useSelector((state) => state.userdata.usersById);
    const portfolios = useSelector((state) => state.portfoliodata.portfolios);
    const isFetchingPortfolios = useSelector((state) => state.portfoliodata.isFetching);
    const tenants = useSelector((state) => state.tenantdata.tenants);
    const userId = parseInt(props.match.params.userId, 10);
    const [user, setUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const userFromStore = usersById[userId];
    const rolesForOrgUsers = User.RolesForOrgUsers();
    const rolesForCustomers = User.RolesForCcUsers();
    const { t } = useTranslation();
    let availableRoles = null;

    useEffect(() => {
        if (usersById[userId] == null && !getUserByIdInProgress) {
            getUser(userId)(dispatch);
        } else if (usersById[userId] != null) {
            setUser(usersById[userId]);
        }
    }, [usersById, getUserByIdInProgress, dispatch, userId]);

    useEffect(() => {
        if (!isFetchingPortfolios && (!portfolios || portfolios.length === 0)) {
            dispatch(listPortfolios());
        }
    }, [portfolios, isFetchingPortfolios, dispatch]);

    useEffect(() => {
        if (!tenants || tenants.length === 0) {
            getTenants()(dispatch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const close = () => {
        props.history.goBack();
    };

    const handleChange = (evt) => {
        const value = evt.target.type === 'checkbox' ? evt.target.checked : evt.target.value;
        const name = evt.target.name;
        let newUser = new User(user);
        switch (name) {
            case 'rolesselect':
                newUser.roles = [];
                for (let r of value) {
                    newUser.roles.push(r);
                }
                break;
            case 'userStatus':
                newUser.enabled = value;
                break;
            case 'userName':
                newUser.name = value;
                break;
            case 'userPhonenumber':
                newUser.phoneNumber = value.trim();
                break;
            case 'userPortfolios':
                newUser.portfolios = value
                    .map((id) => _.find(portfolios, ['id', id]))
                    .filter((p) => p != null)
                    .map((p) => new Portfolio(p));
                break;
            case 'userTenants':
                newUser.tenantCompanies = value
                    .map((clusterId) => _.find(tenants, ['clusterId', clusterId]))
                    .filter((t) => t != null);
                // A portfolio only stays assignable while the user still has access to its tenant company -
                // drop any portfolio that no longer belongs to one of the newly selected tenants.
                {
                    const newTenantClusterIds = newUser.tenantCompanies.map((tc) => tc.clusterId);
                    newUser.portfolios = newUser.portfolios.filter((p) => {
                        const portfolio = _.find(portfolios, ['id', p.id]);
                        return (
                            portfolio == null ||
                            portfolio.tenantCompanyClusterId == null ||
                            newTenantClusterIds.includes(portfolio.tenantCompanyClusterId)
                        );
                    });
                }
                break;
            default:
                break;
        }
        setUser(newUser);
    };

    const save = () => {
        setSaving(true);
        saveUser(user)(dispatch)
            .then(() => {
                setSaving(false);
            })
            .catch(() => {
                setSaving(false);
            });
    };

    const hasChanges = () => {
        return !_.isEqual(user, userFromStore);
    };
    const hasErrors = () => {
        return !verifyPhonenumber(user.phoneNumber);
    };
    const handlePhoneBlur = () => {
        const normalized = normalizePhonenumber(user.phoneNumber);
        if (normalized) {
            const newUser = Object.assign({}, user);
            newUser.phoneNumber = normalized;
            setUser(newUser);
        }
    };
    const passwordReseted = () => {
        setShowResetPassword(false);
    };
    const remove = () => {
        setShowDeleteConfirm(true);
    };
    const doRemoveUser = () => {
        setShowDeleteConfirm(false);
        setSaving(true);
        removeUser(user)(dispatch)
            .then(() => {
                props.history.goBack();
            })
            .catch(() => {
                setSaving(false);
            });
    };

    const repChanged = (newRep) => {
        let newUser = new User(user);
        newUser.representations = newRep;
        setUser(newUser);
    };

    if (user) {
        if (user.isCustomerUser) {
            availableRoles = rolesForCustomers;
        } else {
            availableRoles = rolesForOrgUsers;
        }
    }

    const isAdminOrReporting =
        user != null && (user.roles.includes(User.RoleAdmin()) || user.roles.includes(User.RoleReporting()));

    // Portfolios belong to exactly one tenant company (see doc/sweden-expansion-currency-plan.md). Once the
    // admin picks the tenant(s) a user should have access to, only offer portfolios from those tenants -
    // otherwise a user could get assigned e.g. a Swedish portfolio while only having Finnish tenant access.
    const userTenantClusterIds = user ? user.tenantCompanies.map((tc) => tc.clusterId) : [];
    const availablePortfolios = (portfolios || []).filter(
        (p) => p.tenantCompanyClusterId == null || userTenantClusterIds.includes(p.tenantCompanyClusterId)
    );

    return (
        <Grid container>
            {user == null && getUserByIdInProgress && <em>{t('general.retrievingInformation')}</em>}
            {user != null && (
                <Grid container item>
                    <Grid item xs={12}>
                        <h5>
                            <Button onClick={close}>
                                <i className="fas fa-chevron-left fa-2x"></i>&nbsp;&nbsp;
                            </Button>
                            {user.username}
                        </h5>
                    </Grid>

                    <Grid item xs={12}>
                        <span sx={{ ml: 1, mr: 1, width: '95%' }}>Amazon: {user.amazonStatus}</span>
                    </Grid>

                    {user.isCustomerUser && (
                        <Grid item container>
                            <Grid item xs={12} md={6} sx={{ fontWeight: 'bold' }}>
                                {t('admin.represents')}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RepresentationsList
                                    representations={user.representations}
                                    allowEdit={true}
                                    representationsChanged={repChanged}
                                />
                            </Grid>
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormControl sx={{ mt: 2, ml: 1, mr: 1, width: '95%' }}>
                            <InputLabel id="user-status-label">{t('general.state')}</InputLabel>
                            <Select
                                required
                                error={false}
                                labelId="user-status-label"
                                id="userStatus"
                                name="userStatus"
                                value={user.enabled}
                                onChange={handleChange}>
                                <MenuItem value={false}>{t('general.stateDisabled')}</MenuItem>
                                <MenuItem value={true}>{t('general.stateActive')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={user.name == null || user.name.length === 0}
                            id="userName"
                            name="userName"
                            label={t('general.name')}
                            value={user.name}
                            sx={{ ml: 1, mr: 1, width: '95%' }}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={user.phoneNumber == null || !verifyPhonenumber(user.phoneNumber)}
                            id="userPhonenumber"
                            name="userPhonenumber"
                            label={t('general.phone')}
                            value={user.phoneNumber}
                            sx={{ ml: 1, mr: 1, width: '95%' }}
                            margin="normal"
                            onChange={handleChange}
                            onBlur={handlePhoneBlur}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl sx={{ m: 1, width: '95%' }}>
                            <InputLabel id="rolesLabel">{t('admin.roles')}</InputLabel>
                            <Select
                                labelId="rolesLabel"
                                id="rolesselect"
                                name="rolesselect"
                                multiple
                                value={user.roles.map((role) => role)}
                                onChange={handleChange}
                                input={<Input id="selectRoles" />}
                                renderValue={() => (
                                    <div sx={styles.chips}>
                                        {user.roles.map((role) => (
                                            <Chip key={role} label={role} sx={styles.chip} />
                                        ))}
                                    </div>
                                )}
                                MenuProps={MenuProps}>
                                {availableRoles.map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {!user.isCustomerUser && (
                        <Grid item container>
                            <Grid item xs={12} md={6}>
                                <FormControl sx={{ m: 1, width: '95%' }}>
                                    <InputLabel id="userPortfoliosLabel">{t('admin.portfolios')}</InputLabel>
                                    <Select
                                        labelId="userPortfoliosLabel"
                                        id="userPortfolios"
                                        name="userPortfolios"
                                        multiple
                                        disabled={isAdminOrReporting || userTenantClusterIds.length === 0}
                                        value={user.portfolios.map((p) => p.id)}
                                        onChange={handleChange}
                                        input={<Input id="selectUserPortfolios" />}
                                        renderValue={() => (
                                            <div sx={styles.chips}>
                                                {user.portfolios.map((p) => (
                                                    <Chip key={p.id} label={p.name} sx={styles.chip} />
                                                ))}
                                            </div>
                                        )}
                                        MenuProps={MenuProps}>
                                        {availablePortfolios.map((p) => (
                                            <MenuItem key={p.id} value={p.id}>
                                                {p.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {!isAdminOrReporting && userTenantClusterIds.length === 0 && (
                                        <em sx={{ ml: 1 }}>{t('admin.selectTenantFirst')}</em>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl sx={{ m: 1, width: '95%' }}>
                                    <InputLabel id="userTenantsLabel">{t('admin.tenants')}</InputLabel>
                                    <Select
                                        labelId="userTenantsLabel"
                                        id="userTenants"
                                        name="userTenants"
                                        multiple
                                        disabled={isAdminOrReporting}
                                        value={user.tenantCompanies.map((tc) => tc.clusterId)}
                                        onChange={handleChange}
                                        input={<Input id="selectUserTenants" />}
                                        renderValue={() => (
                                            <div sx={styles.chips}>
                                                {user.tenantCompanies.map((tc) => (
                                                    <Chip key={tc.clusterId} label={tc.name} sx={styles.chip} />
                                                ))}
                                            </div>
                                        )}
                                        MenuProps={MenuProps}>
                                        {(tenants || []).map((tc) => (
                                            <MenuItem key={tc.clusterId} value={tc.clusterId}>
                                                {tc.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {isAdminOrReporting && (
                                <Grid item xs={12}>
                                    <em sx={{ ml: 1 }}>{t('admin.accessAllPortfoliosAndTenantsNote')}</em>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Grid>
            )}
            <Footer>
                <Grid item>
                    <Button
                        variant="contained"
                        name="poista"
                        color="secondary"
                        onClick={remove}
                        disabled={saving || !userFromStore || userFromStore.enabled}>
                        <i className="fas fa-trash"></i> &nbsp;{t('buttons.remove')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="btn-save"
                        disabled={saving || !user || !hasChanges() || hasErrors()}
                        onClick={save}>
                        <i className="fas fa-save"></i>&nbsp;{t('buttons.save')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="btn-reset-password"
                        onClick={() => setShowResetPassword(true)}
                        disabled={saving || !user}>
                        &nbsp;{t('admin.resetPassword')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="secondary"
                        name="btn-close"
                        disabled={saving || !user}
                        onClick={close}>
                        {t('buttons.close')}
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
            <ResetPasswordDialog
                open={showResetPassword}
                user={user}
                passwordReseted={passwordReseted}
                cancel={() => setShowResetPassword(false)}
            />
            {user && (
                <DeleteConfirmDialog
                    open={showDeleteConfirm}
                    itemToDelete={`käyttäjän ${user.username}`}
                    cancel={() => setShowDeleteConfirm(false)}
                    confirmDelete={doRemoveUser}
                />
            )}
        </Grid>
    );
}
